import { useCallback, useMemo } from "react";
import { Stack, useLocalSearchParams, useRouter } from "expo-router";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { ChangeRoutineContent } from "@src/features/alarms/components/ChangeRoutineContent";
import {
  deriveSuggestionLabel,
  formatRoutineForDisplay,
  getSuggestionAction,
} from "@src/features/alarms/utils/format-routine";
import { useAlarmsByCameras, useRoutineSuggestions } from "@src/services/api/hooks";
import { buildAlarmSettingsRoutes } from "@src/features/alarms/utils/alarm-settings-routes";
import type { AlarmRoutine, RoutineSuggestion } from "@src/services/api/types";
import { useUnistyles } from "react-native-unistyles";
import { useHeaderBackButton } from "@src/hooks/useHeaderBackButton";

export default function ChangeRoutineScreen() {
  const { bottom } = useSafeAreaInsets();
  const router = useRouter();
  const {
    tenantId,
    siteId,
    cameraKeys: cameraKeysParam,
  } = useLocalSearchParams<{
    tenantId: string;
    siteId: string;
    cameraKeys: string;
  }>();
  const { theme } = useUnistyles();
  const { headerLeft } = useHeaderBackButton();
  const routes = useMemo(() => buildAlarmSettingsRoutes(tenantId, siteId), [tenantId, siteId]);

  const cameraKeys = useMemo(() => {
    if (!cameraKeysParam) return [];
    try {
      return JSON.parse(cameraKeysParam) as string[];
    } catch {
      return [];
    }
  }, [cameraKeysParam]);

  const alarmQueries = useAlarmsByCameras(siteId, cameraKeys);
  const firstCameraKey = cameraKeys[0];
  const suggestionsQuery = useRoutineSuggestions(siteId, firstCameraKey);

  const isLoading = alarmQueries.some((q) => q.isLoading) || suggestionsQuery.isLoading;
  const suggestions = suggestionsQuery.data;

  const alarmSettings = alarmQueries.map((q) => q.data?.settings);
  const settingsFingerprint = alarmSettings.map((s) => (s?.routine ? JSON.stringify(s.routine) : "")).join("|");

  const currentRoutines = useMemo(() => {
    const seen = new Map<string, AlarmRoutine>();
    for (const settings of alarmSettings) {
      const routine = settings?.routine;
      if (routine) {
        const displayKey = formatRoutineForDisplay(routine)
          .map((w) => `${w.name}:${w.timeRanges}`)
          .join("|");
        if (!seen.has(displayKey)) {
          seen.set(displayKey, routine);
        }
      }
    }
    return Array.from(seen.values());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsFingerprint]);

  const processedSuggestions = useMemo((): (RoutineSuggestion & { action: "armed" | "disarmed" })[] => {
    if (!suggestions || !Array.isArray(suggestions)) return [];
    return (suggestions as RoutineSuggestion[]).map((suggestion) => ({
      ...suggestion,
      action: getSuggestionAction(suggestion),
      name: deriveSuggestionLabel(suggestion),
    }));
  }, [suggestions]);

  const navigateToCustomize = useCallback(
    (routine?: AlarmRoutine) => {
      const params: Record<string, string> = {
        cameraKeys: JSON.stringify(cameraKeys),
      };
      if (routine) {
        params.routine = encodeURIComponent(JSON.stringify(routine));
      }
      router.push({
        pathname: routes.editRoutine as never,
        params,
      });
    },
    [cameraKeys, router, routes],
  );

  return (
    <>
      <Stack.Screen options={{ title: "Change routine", headerLeft }} />
      <ChangeRoutineContent
        backgroundColor={theme.colors.background}
        isLoading={isLoading}
        currentRoutines={currentRoutines}
        suggestions={processedSuggestions}
        onEdit={navigateToCustomize}
        bottomInset={bottom}
      />
    </>
  );
}
